import React, { useEffect, useRef, useState } from 'react';

import { DateTime } from 'luxon';

import { Button, Col, Panel, Row, Table, NavLinkButton } from '@shared/components/bootstrap';
import { FieldFormGroup } from '@shared/components/fields/field_form_group';
import { useDebounce } from '@shared/hooks';
import { useArrayURLSearchParam } from '@shared/hooks/use_array_url_search_param';
import { useBooleanURLSearchParam } from '@shared/hooks/use_boolean_url_search_param';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';
import { useURLSearchParams } from '@shared/hooks/use_url_search_params';

import { CustomerNames as AccountCustomerNames } from '@admin/components/account/customer_names';
import { Preview as AddressPreview } from '@admin/components/address/preview';
import { InputFormGroup } from '@admin/components/fields/input_form_group';
import { MultiselectFormControl } from '@admin/components/fields/multiselect_form_control';
import { SelectFormGroup } from '@admin/components/fields/select_form_group';
import { Label } from '@admin/components/helpers/label';
import { FormattedServiceType } from '@admin/components/orders/formatted_service_type';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { orderURL, accountURL } from '@admin/config/routes';
import {
  OrderServiceParam,
  ORDER_SERVICES,
  ORDER_SERVICE_TO_NAME,
  ORDER_SERVICE_TO_PARAM,
  ORDER_SERVICE_FROM_PARAM,
  ORDER_SERVICE_TO_STATUS,
} from '@admin/constants/order_services';
import {
  OrderStateParam,
  ORDER_STATES,
  ORDER_STATE_TO_NAME,
  ORDER_STATE_TO_PARAM,
  ORDER_STATE_FROM_PARAM,
} from '@admin/constants/order_state';
import {
  ORDER_SUBTYPES,
  ORDER_SUBTYPE_TO_NAME,
  ORDER_SUBTYPE_TO_PARAM,
  ORDER_SUBTYPE_FROM_PARAM,
  OrderSubtypeParam,
} from '@admin/constants/order_subtype';
import {
  ORDER_TYPES,
  ORDER_TYPE_TO_NAME,
  ORDER_TYPE_TO_PARAM,
  ORDER_TYPE_FROM_PARAM,
  OrderTypeParam,
  ORDER_TYPE_TO_STATUS,
} from '@admin/constants/order_type';
import { useQA } from '@admin/hooks/use_qa';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Scalars,
  Order,
  OrderStateEnum as Order__State,
  OrderServiceTypeEnum as Order__Service,
  OrderSubtypeEnum as Order__Subtype,
  OrderTypeEnum as Order__Type,
  Region,
  Order__Policy,
  usePaginatedIndexOrdersQuery,
  useRegionsSelectorQuery,
} from '@admin/schema';

import { Scheduled } from './scheduled';
import { State } from './state';
import { Summary } from './summary';

const DEFAULT_PAGE = 1;

const ORDER_STATE_OPTIONS = ORDER_STATES.map((state) => ({
  id: state,
  name: ORDER_STATE_TO_NAME[state],
}));

type DispatchedParam = 'no' | 'yes';

const DISPATCHED_FROM_PARAM: Record<DispatchedParam, boolean> = {
  no: false,
  yes: true,
};

const dispatchedToParam = (dispatched?: boolean) => (dispatched === undefined ? undefined : dispatched ? 'yes' : 'no');

const SearchFormGroup: React.FC<{
  query?: string;
  onChange(query?: string): void;
}> = ({ query, onChange }) => (
  <InputFormGroup
    label="Search"
    type="text"
    placeholder="Search by name, email, phone, address, account # or order #"
    value={query ?? ''}
    onChange={(event) => onChange(event.target.value || undefined)}
  />
);

const DateFormGroup: React.FC<{
  date?: string; // e.g. "2022-12-31" (ISO formatted)
  onChange(date?: string): void; // e.g. "2020-12-31" (ISO formatted)
}> = ({ date, onChange }) => (
  <InputFormGroup
    label="Date:"
    type="date"
    value={date ?? ''}
    onChange={(event) => onChange(event.target.value || undefined)}
  />
);

const RegionsFormGroup: React.FC<{
  regionIDs: string[];
  onChange(regionIDs: string[]): void;
}> = ({ regionIDs, onChange }) => {
  const { data } = useRegionsSelectorQuery({ client });
  const options = data?.regions;

  return (
    <FieldFormGroup label="Region:">
      <MultiselectFormControl
        placeholder="- Region -"
        selectedIDs={regionIDs}
        onChange={(selectedIDs) => onChange(selectedIDs)}
        options={options ?? []}
        disabled={!options}
      />
    </FieldFormGroup>
  );
};

const ACTIVE_ORDER_TYPES = ORDER_TYPES.filter((type) => ORDER_TYPE_TO_STATUS[type] === 'active');
const LEGACY_ORDER_TYPES = ORDER_TYPES.filter((type) => ORDER_TYPE_TO_STATUS[type] === 'legacy');

const TypeFormGroup: React.FC<{
  type?: Order__Type;
  onChange(type?: Order__Type): void;
}> = ({ type, onChange }) => (
  <SelectFormGroup
    label="Type:"
    value={type ?? ''}
    onChange={(event) => onChange((event.target.value as Order__Type) || undefined)}
  >
    <option value="">- Type -</option>
    {ACTIVE_ORDER_TYPES.map((value) => (
      <option key={value} value={value}>
        {ORDER_TYPE_TO_NAME[value]}
      </option>
    ))}
    <optgroup label="Legacy">
      {LEGACY_ORDER_TYPES.map((value) => (
        <option key={value} value={value}>
          {ORDER_TYPE_TO_NAME[value]}
        </option>
      ))}
    </optgroup>
  </SelectFormGroup>
);

const SubtypeFormGroup: React.FC<{
  subtype?: Order__Subtype;
  onChange(subtype?: Order__Subtype): void;
}> = ({ subtype, onChange }) => (
  <SelectFormGroup
    label="Subtype:"
    value={subtype ?? ''}
    onChange={(event) => onChange((event.target.value as Order__Subtype) || undefined)}
  >
    <option value="">- Subtype -</option>
    {ORDER_SUBTYPES.map((value) => (
      <option key={value} value={value}>
        {ORDER_SUBTYPE_TO_NAME[value]}
      </option>
    ))}
  </SelectFormGroup>
);

const StatesFormGroup: React.FC<{
  states: Order__State[];
  onChange(states: Order__State[]): void;
}> = ({ states, onChange }) => (
  <FieldFormGroup label="State:">
    <MultiselectFormControl
      placeholder="- State -"
      selectedIDs={states}
      onChange={(changes) => onChange(changes)}
      options={ORDER_STATE_OPTIONS}
    />
  </FieldFormGroup>
);

const DispatchedFormGroup: React.FC<{
  dispatched?: boolean;
  onChange(dispatched?: boolean): void;
}> = ({ dispatched, onChange }) => (
  <SelectFormGroup
    label="Dispatched:"
    value={dispatchedToParam(dispatched)}
    onChange={(event) => {
      onChange(DISPATCHED_FROM_PARAM[event.target.value as DispatchedParam]);
    }}
  >
    <option value="">- Dispatched -</option>
    <option value="yes">Yes</option>
    <option value="no">No</option>
  </SelectFormGroup>
);

const ACTIVE_ORDER_SERVICES = ORDER_SERVICES.filter((service) => ORDER_SERVICE_TO_STATUS[service] === 'active');
const LEGACY_ORDER_SERVICES = ORDER_SERVICES.filter((service) => ORDER_SERVICE_TO_STATUS[service] === 'legacy');

const ServiceFormGroup: React.FC<{
  service?: Order__Service;
  onChange(service?: Order__Service): void;
}> = ({ service, onChange }) => (
  <SelectFormGroup
    label="Service:"
    value={service ?? ''}
    onChange={(event) => onChange((event.target.value as Order__Service) || undefined)}
  >
    <option value="">- Service -</option>
    {ACTIVE_ORDER_SERVICES.map((value) => (
      <option key={value} value={value}>
        {ORDER_SERVICE_TO_NAME[value]}
      </option>
    ))}
    <optgroup label="Legacy">
      {LEGACY_ORDER_SERVICES.map((value) => (
        <option key={value} value={value}>
          {ORDER_SERVICE_TO_NAME[value]}
        </option>
      ))}
    </optgroup>
  </SelectFormGroup>
);

const Dispatcher: React.FC<{
  order: Pick<Order, 'id' | 'state' | 'scheduled' | 'dispatched'> & {
    region: Pick<Region, 'id' | 'parentID' | 'tz'>;
    policy: Pick<Order__Policy, 'dispatch'>;
  };
}> = ({ order }) => {
  if (order.state === Order__State.Canceled)
    return (
      <Button disabled kind="default">
        Canceled
      </Button>
    );
  if (order.state === Order__State.Completed)
    return (
      <Button disabled kind="default">
        Completed
      </Button>
    );
  if (order.dispatched)
    return (
      <Button disabled kind="default">
        Dispatched
      </Button>
    );
  if (!order.policy.dispatch) return null;

  const region = order.region;
  const scheduled = DateTime.fromISO(order.scheduled, { zone: region.tz });
  const regionID = region.parentID ?? region.id;
  const date = scheduled.toFormat('yyyy-MM-dd');
  const dispatcherURL = `/dispatcher?date=${date}&region_id=${regionID}`;

  return (
    <NavLinkButton kind="default" to={dispatcherURL}>
      Dispatch
    </NavLinkButton>
  );
};

export const OrdersTable: React.FC = () => {
  useTitle('Orders');
  const qa = useQA();

  const [_params, setParams] = useURLSearchParams();
  const [page, setPage] = useNumericURLSearchParam('page');
  const [query] = useURLSearchParam('query');
  const [date] = useURLSearchParam('date'); // e.g. ISO formatted "2022-12-31"
  const [type] = useURLSearchParam<OrderTypeParam>('type');
  const [subtype] = useURLSearchParam<OrderSubtypeParam>('subtype');
  const [service] = useURLSearchParam<OrderServiceParam>('service');
  const [dispatched] = useBooleanURLSearchParam('dispatched');
  const [states] = useArrayURLSearchParam<OrderStateParam>('state');
  const [regionIDs] = useArrayURLSearchParam('region_id');

  const { data, loading } = usePaginatedIndexOrdersQuery({
    client: client,
    variables: {
      page: page ?? DEFAULT_PAGE,
      filters: {
        query: useDebounce(query),
        date,
        type: type && ORDER_TYPE_FROM_PARAM[type],
        subtype: subtype && ORDER_SUBTYPE_FROM_PARAM[subtype],
        service: service && ORDER_SERVICE_FROM_PARAM[service],
        dispatched,
        states: states.length > 0 ? states.map((state) => ORDER_STATE_FROM_PARAM[state]) : undefined,
        regionIDs: regionIDs.length > 0 ? regionIDs : undefined,
      },
    },
  });

  const orders = data?.paginatedOrders.results;
  const pagination = data?.paginatedOrders.pagination;

  return (
    <>
      <Panel>
        <Panel.Body>
          <form>
            <Row>
              <Col sm={8}>
                <SearchFormGroup
                  query={query}
                  onChange={(query) => {
                    setParams((params) => {
                      params.delete('page');
                      if (query) params.set('query', query);
                      else params.delete('query');
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={4}>
                <DateFormGroup
                  date={date}
                  onChange={(date) => {
                    setParams((params) => {
                      params.delete('page');
                      if (date) params.set('date', date);
                      else params.delete('date');
                      return params;
                    });
                  }}
                />
              </Col>
            </Row>

            <Row>
              <Col sm={2}>
                <RegionsFormGroup
                  regionIDs={regionIDs}
                  onChange={(regionIDs) => {
                    setParams((params) => {
                      params.delete('page');
                      params.delete('region_id');
                      if (regionIDs) {
                        for (const regionID of regionIDs) {
                          params.append('region_id', regionID);
                        }
                      }
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={2}>
                <TypeFormGroup
                  type={type && ORDER_TYPE_FROM_PARAM[type]}
                  onChange={(type) => {
                    setParams((params) => {
                      params.delete('page');
                      if (type) params.set('type', ORDER_TYPE_TO_PARAM[type]);
                      else params.delete('type');
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={2}>
                <SubtypeFormGroup
                  subtype={subtype && ORDER_SUBTYPE_FROM_PARAM[subtype]}
                  onChange={(subtype) => {
                    setParams((params) => {
                      params.delete('page');
                      if (subtype) params.set('subtype', ORDER_SUBTYPE_TO_PARAM[subtype]);
                      else params.delete('subtype');
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={2}>
                <StatesFormGroup
                  states={states.map((state) => ORDER_STATE_FROM_PARAM[state])}
                  onChange={(states) => {
                    setParams((params) => {
                      params.delete('page');
                      params.delete('state');
                      if (states) {
                        for (const state of states) {
                          params.append('state', ORDER_STATE_TO_PARAM[state]);
                        }
                      }
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={2}>
                <DispatchedFormGroup
                  dispatched={dispatched}
                  onChange={(dispatched) => {
                    setParams((params) => {
                      params.delete('page');
                      if (typeof dispatched !== 'undefined') params.set('dispatched', dispatched ? 'yes' : 'no');
                      else params.delete('dispatched');
                      return params;
                    });
                  }}
                />
              </Col>
              <Col sm={2}>
                <ServiceFormGroup
                  service={service ? ORDER_SERVICE_FROM_PARAM[service] : undefined}
                  onChange={(service) => {
                    setParams((params) => {
                      params.delete('page');
                      if (service) params.set('service', ORDER_SERVICE_TO_PARAM[service]);
                      else params.delete('service');
                      return params;
                    });
                  }}
                />
              </Col>
            </Row>

            {qa && (
              <div className="clearfix">
                <div className="pull-right">
                  <NavLinkButton kind="primary" to="/test_orders">
                    QA Test Orders
                  </NavLinkButton>
                </div>
              </div>
            )}
          </form>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body>
          <Table striped responsive>
            <thead>
              <tr>
                <th className="col-sm-3">Details</th>
                <th className="col-sm-3">Subscription</th>
                <th className="col-sm-3">Customer</th>
                <th className="col-sm-3">Address</th>
                <th className="col-zero"></th>
                <th className="col-zero"></th>
              </tr>
            </thead>

            <tbody>
              {orders?.map((order) => (
                <tr key={order.id}>
                  <td className="col-sm-3">
                    <div>
                      <span>#{order.number}</span>
                      <span> - </span>
                      <Summary order={order} />
                      <span> - </span>
                      <span>
                        <strong>{order.movers}</strong> movers
                      </span>
                    </div>
                    <div>
                      <FormattedServiceType order={order} />
                    </div>
                    <div>
                      <span>Scheduled:</span>{' '}
                      <span>
                        <Scheduled order={order} />
                      </span>
                    </div>
                    <div>
                      <State state={order.state} waitlisted={order.waitlistRequests.length > 0} />
                    </div>
                    {!!order.flaggedAt && (
                      <div>
                        <Label kind="default">
                          Modified on {DateTime.fromISO(order.flaggedAt).toLocaleString(DateTime.DATETIME_MED)}
                        </Label>
                      </div>
                    )}
                  </td>
                  <td className="col-sm-3">
                    {order.account.subscriptions.map((subscription) => (
                      <div key={subscription.id}>
                        {!subscription.custom && <>{subscription.quantity} × </>} {subscription.name}
                      </div>
                    ))}
                  </td>
                  <td className="col-sm-3">
                    <a href={accountURL(order.account)} target="_blank">
                      <AccountCustomerNames account={order.account} />
                    </a>
                  </td>
                  <td className="col-sm-3">
                    <a href={order.address.map} target="_blank">
                      <AddressPreview address={order.address} />
                    </a>
                  </td>
                  <td className="col-zero text-center">
                    <Dispatcher order={order} />
                  </td>
                  <td className="col-zero text-center">
                    <NavLinkButton kind="primary" to={orderURL(order)}>
                      View
                    </NavLinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {loading && <Spinner />}
          {pagination && <Pagination pagination={pagination} onPage={setPage} />}
        </Panel.Body>
      </Panel>
    </>
  );
};
